/**
 * TODO:
 * - Add missing proptypes
 */
import { useTheme } from '@emotion/react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import Block from 'shopper/components/Block';
import Box from 'shopper/components/Box';
import Flex from 'shopper/components/Flex';
import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import FollowUserButton from 'components/FollowUserButton';
import TextWithRichContent from 'components/TextWithRichContent';
import UserAvatar from 'components/UserAvatar';

import { formatDateByDayMonthYear } from 'lib/date';
import toOfferAdditionalInfosText from 'lib/htmlParsers/parseOfferAdditionalInfos';
import { toUserProfileLink } from 'lib/links';
import { capitalizeEveryWord } from 'lib/string';

import HEADING from 'constants/heading';

const Collapse = dynamic(() => import('shopper/components/Collapse'), {
  ssr: false,
});
const UserFirstOfferAlert = dynamic(() => import('./UserFirstOfferAlert'), {
  ssr: false,
});
const UserSpecialty = dynamic(() => import('components/UserSpecialty'), {
  ssr: false,
});

const OfferAdditionalInfos = ({
  gaEventCategory,
  offerFaq,
  offerInstructions,
  offerPublished,
  offerUserDescription,
  userId,
  userUsername,
  userPhoto,
  userName,
  userSpecialtyName,
  userFirstOffer: isUserFirstOffer,
}) => {
  const theme = useTheme();

  const parsedOfferInstructions = useMemo(
    () =>
      offerInstructions ? toOfferAdditionalInfosText(offerInstructions) : null,
    [offerInstructions]
  );

  const parsedOfferUserDescription = useMemo(
    () =>
      offerUserDescription
        ? toOfferAdditionalInfosText(offerUserDescription)
        : null,
    [offerUserDescription]
  );

  return (
    <Flex as="section" mb={4} full>
      <Box borderTop="0.25rem solid" borderColor={theme.app.offer.border} full>
        <Flex
          alignItems="flex-start"
          flexDirection="column"
          py={4}
          px={[4, null, 6]}
        >
          <Heading size="size4" as="h2" mb={4}>
            {HEADING.ADDITIONAL_INFO}
          </Heading>
          {parsedOfferInstructions && (
            <Block mb={5}>{parsedOfferInstructions}</Block>
          )}
          {offerFaq.length > 0 &&
            offerFaq.map(({ title, text }) => (
              <Collapse key={title} title={title} mb={2} full>
                <TextWithRichContent>{text}</TextWithRichContent>
              </Collapse>
            ))}
        </Flex>
        {parsedOfferUserDescription && (
          <Flex
            borderTop="1px solid"
            flexDirection="column"
            borderColor={theme.app.offer.dividerBg}
            py={5}
            px={[4, null, 6]}
            full
          >
            <Text mb={1} bold>
              Opinião do autor:
            </Text>
            {parsedOfferUserDescription}
          </Flex>
        )}
        <Flex
          borderTop="1px solid"
          flexDirection="column"
          borderColor={theme.app.offer.dividerBg}
          py={3}
          px={[4, null, 6]}
          full
        >
          <Flex alignItems="center" flexWrap="wrap" full>
            <Flex alignItems="center" flex="1">
              <NextLink
                href={toUserProfileLink(userUsername)}
                prefetch={false}
                passHref
              >
                <Anchor>
                  <UserAvatar
                    loading="lazy"
                    userName={userName}
                    userPhoto={userPhoto}
                  />
                </Anchor>
              </NextLink>
              <Flex flexDirection="column" ml={4}>
                <Text as="span">
                  {`Oferta publicada por `}
                  <NextLink
                    href={toUserProfileLink(userUsername)}
                    prefetch={false}
                    passHref
                  >
                    <Link>{capitalizeEveryWord(userName)}</Link>
                  </NextLink>
                  {` em ${formatDateByDayMonthYear(offerPublished)}`}.
                </Text>
                {userSpecialtyName && (
                  <UserSpecialty
                    userId={userId}
                    userSpecialtyName={userSpecialtyName}
                  />
                )}
              </Flex>
            </Flex>
            <FollowUserButton
              gaEventCategory={gaEventCategory}
              userId={userId}
              mt={[4, null, 0]}
            />
          </Flex>
          {isUserFirstOffer && (
            <UserFirstOfferAlert userName={capitalizeEveryWord(userName)} />
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

OfferAdditionalInfos.defaultProps = {
  offerInstructions: null,
  userPhoto: null,
  userSpecialtyName: null,
};

OfferAdditionalInfos.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  offerInstructions: PropTypes.string,
  userUsername: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  userPhoto: PropTypes.string,
  userName: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userFirstOffer: PropTypes.bool.isRequired,
  offerPublished: PropTypes.string.isRequired,
};

export default OfferAdditionalInfos;
